import { useEffect, useRef, useState } from "react";

/**
 * Adds `data-revealed="true"` to the element once it scrolls into view.
 * Pair with CSS that targets `[data-reveal]` (initial) and `[data-revealed="true"]`.
 * Respects prefers-reduced-motion (reveals immediately).
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(options?: {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}) {
  const ref = useRef<T | null>(null);
  const { threshold = 0.12, rootMargin = "0px 0px -8% 0px", once = true } = options ?? {};

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      el.setAttribute("data-revealed", "true");
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).setAttribute("data-revealed", "true");
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            (entry.target as HTMLElement).removeAttribute("data-revealed");
          }
        }
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin, once]);

  return ref;
}

/**
 * Tracks which section id is currently in view (for nav highlighting).
 * Pass section ids in DOM order. Returns the active id (or null).
 */
export function useActiveSection(ids: string[]) {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visible = new Map<string, number>();

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) visible.set(id, e.intersectionRatio);
            else visible.delete(id);
          }
          let best: string | null = null;
          let bestRatio = 0;
          visible.forEach((r, k) => {
            if (r > bestRatio) { bestRatio = r; best = k; }
          });
          setActive((prev) => (prev === best ? prev : best));
        },
        { threshold: [0, 0.25, 0.5, 0.75], rootMargin: "-30% 0px -50% 0px" },
      );
      io.observe(el);
      observers.push(io);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [ids.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  return active;
}